import React, { useState } from "react";
import { Heart, Eye, ShoppingCart } from "lucide-react";

// Base & Variant Images
import product1 from "../../../../assets/images/mendropdown/shootingtblue1.webp";
import product1Blue from "../../../../assets/images/mendropdown/shootingwhite1.webp";
import product1grey from "../../../../assets/images/mendropdown/shottingjacketblue2.webp";
import product1darkblue from "../../../../assets/images/mendropdown/cap3blue.webp";
import product2 from "../../../../assets/images/mendropdown/cap3white.webp";
import product2red from "../../../../assets/images/mendropdown/cap4blue.webp";
import product2black from "../../../../assets/images/mendropdown/cap4white.webp";
import product2blue from "../../../../assets/images/mendropdown/Sleevless4blue.webp";
import product3 from "../../../../assets/images/mendropdown/Sleevless4green.webp";
import product4 from "../../../../assets/images/mendropdown/Sleevless5.webp";
import product5 from "../../../../assets/images/mendropdown/Sleevless5blue.webp";
import product6 from "../../../../assets/images/mendropdown/Sleevless6.webp";


// Products with variants
const productsData = [
  {
    id: 1,
    name: "Striped Blue T-Shirt",
    category: "T-Shirts",
    price: 1099,
    oldPrice: 1299,
    rating: 5.5,
    image: product1,
    variants: [
      { color: "bg-blue-600", image: product1 },
      { color: "bg-white", image: product1Blue },
    ],
  },
  {
    id: 2,
    name: "Wave Print Tee",
    category: "Polos",
    price: 999,
    oldPrice: 1199,
    rating: 5.4,
    image: product2,
    variants: [
      { color: "bg-red-500", image: product2red },
      { color: "bg-black", image: product2black },
      { color: "bg-blue-600", image: product2blue },
    ],
  },
  // Duplicate entries reusing variants for brevity
  {
    id: 3,
    name: "Wave Print Tee",
    category: "T-Shirts",
    price: 999,
    oldPrice: 1199,
    rating: 4.5,
    image: product3,
    variants: [
      { color: "bg-red-500", image: product2red },
      { color: "bg-black", image: product2black },
      { color: "bg-blue-600", image: product2blue },
    ],
  },
  {
    id: 4,
    name: "Wave Print Tee",
    category: "Polos",
    price: 999,
    oldPrice: 1199,
    rating: 3.2,
    image: product4,
    variants: [
      { color: "bg-red-500", image: product2red },
      { color: "bg-black", image: product2black },
      { color: "bg-blue-600", image: product2blue },
    ],
  },
  {
    id: 5,
    name: "Striped Blue T-Shirt",
    category: "Shirts",
    price: 1099,
    oldPrice: 1299,
    rating: 3.3,
    image: product5,
    variants: [
      { color: "bg-gray-500", image: product1grey },
      { color: "bg-blue-600", image: product1Blue },
      { color: "bg-blue-400", image: product1darkblue },
    ],
  },
  {
    id: 6,
    name: "Striped Blue T-Shirt",
    category: "Shirts",
    price: 1099,
    oldPrice: 1299,
    rating: 3.2,
    image: product6,
    variants: [
      { color: "bg-gray-500", image: product1grey },
      { color: "bg-blue-600", image: product1Blue },
      { color: "bg-blue-400", image: product1darkblue },
    ],
  },
  {
    id: 7,
    name: "Striped Blue T-Shirt",
    category: "T-Shirts",
    price: 1099,
    oldPrice: 1299,
    rating: 4.4,
    image: product1,
    variants: [
      { color: "bg-gray-500", image: product1grey },
      { color: "bg-blue-600", image: product1Blue },
      { color: "bg-blue-400", image: product1darkblue },
    ],
  },
];

const MenShooting = () => {
  const [hoverImages, setHoverImages] = useState({});
  const [sortBy, setSortBy] = useState("Latest");
  const [filterBy, setFilterBy] = useState("All");
  const [relatedHoverImages, setRelatedHoverImages] = useState({});

  const handleRelatedColorHover = (productId, image) => {
    setRelatedHoverImages((prev) => ({ ...prev, [productId]: image }));
  };

  const handleRelatedColorLeave = (productId) => {
    setRelatedHoverImages((prev) => ({ ...prev, [productId]: null }));
  };

  const handleColorHover = (productId, image) => {
    setHoverImages((prev) => ({ ...prev, [productId]: image }));
  };

  const handleColorLeave = (productId) => {
    setHoverImages((prev) => ({ ...prev, [productId]: null }));
  };

  const filteredProducts = productsData.filter((product) =>
    filterBy === "All" ? true : product.category === filterBy
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case "Price: Low to High":
        return a.price - b.price;
      case "Price: High to Low":
        return b.price - a.price;
      case "Average Rating":
        return b.rating - a.rating;
      case "Popularity":
        return b.rating - a.rating;
      default:
        return 0;
    }
  });

  const colorBar = (product, isRelated = false) => (
    <div className="flex h-[4px] w-full mt-1">
      {product.variants?.map((variant, index) => (
        <div
          key={index}
          className={`w-full h-full cursor-pointer ${variant.color}`}
          onMouseEnter={() =>
            isRelated
              ? handleRelatedColorHover(product.id, variant.image)
              : handleColorHover(product.id, variant.image)
          }
          onMouseLeave={() =>
            isRelated
              ? handleRelatedColorLeave(product.id)
              : handleColorLeave(product.id)
          }
        />
      ))}
    </div>
  );

  const colorBars = (
    <div className="flex h-[4px] w-full">
      <div className="w-1/3 bg-red-500" />
      <div className="w-1/3 bg-cyan-500" />
      <div className="w-1/3 bg-blue-700" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      {/* Header and Sort/Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <h2 className="text-xl font-semibold">Showing all results</h2>
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <select
            onChange={(e) => setSortBy(e.target.value)}
            value={sortBy}
            className="border p-2 rounded w-full sm:w-60"
          >
            <option value="Latest">Sort by Latest</option>
            <option value="Popularity">Sort by Popularity</option>
            <option value="Average Rating">Sort by Average Rating</option>
            <option value="Price: Low to High">
              Sort by Price: Low to High
            </option>
            <option value="Price: High to Low">
              Sort by Price: High to Low
            </option>
          </select>
          <select
            onChange={(e) => setFilterBy(e.target.value)}
            value={filterBy}
            className="border p-2 rounded w-full sm:w-60"
          >
            <option value="All">Filter by Product</option>
            <option value="T-Shirts">T-Shirts</option>
            <option value="Polos">Polos</option>
            <option value="Shirts">Shirts</option>
            <option value="Hoodies">Hoodies</option>
          </select>
        </div>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
        {sortedProducts.map((product) => (
          <div key={product.id} className="relative group">
            <div className="relative">
              <img
                src={
                  hoverImages[product.id] ||
                  relatedHoverImages[product.id] ||
                  product.image
                }
                alt={product.name}
                className="w-full h-[220px] sm:h-[260px] md:h-[280px] lg:h-[430px] object-contain"
              />
              <div className="absolute top-2 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition">
                <button className="bg-white p-1 rounded shadow">
                  <Heart size={16} />
                </button>
                <button className="bg-white p-1 rounded shadow">
                  <Eye size={16} />
                </button>
                <button className="bg-white p-1 rounded shadow">
                  <ShoppingCart size={16} />
                </button>
              </div>
            </div>
            {colorBar(product)}
            <div className="mt-2 text-left">
              <h4 className="text-sm font-semibold">{product.name}</h4>
              <div className="flex gap-2 text-sm mt-1">
                <span className="text-red-600 font-bold">₹{product.price}</span>
                <span className="line-through text-gray-500">
                  ₹{product.oldPrice}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Related Section */}
      <div className="mt-24">
        <h3 className="text-lg font-semibold mb-4">Related Products</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
          {productsData.slice(0, 4).map((item) => (
            <div key={item.id} className="relative group">
              <div className="relative">
                <img
                  src={relatedHoverImages[item.id] || item.image}
                  alt={item.name}
                  className="w-full h-[220px] sm:h-[280px] md:h-[260px] lg:h-[430px] object-contain"
                />
                <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition">
                  <button className="bg-white p-1 rounded shadow">
                    <Heart size={16} />
                  </button>
                  <button className="bg-white p-1 rounded shadow">
                    <Eye size={16} />
                  </button>
                  <button className="bg-white p-1 rounded shadow">
                    <ShoppingCart size={16} />
                  </button>
                </div>
              </div>
              {colorBars}
              <div className="mt-2 text-left">
                <h4 className="text-sm font-semibold">{item.name}</h4>
                <p className="text-red-600 font-bold mt-1">₹{item.price}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MenShooting;
